import { useEffect, useState } from 'react';
import {
  digitizationWorkflow,
  type DigitizationProject,
  type DigitizationBatch,
  type ProjectPriority,
} from '@/services/registry';
import styles from './DigitizationPage.module.css';

// Mock data generators
function generateMockProjects(): DigitizationProject[] {

  return [
    {
      id: 'proj_001',
      name: '2024 Personnel Files Digitization',
      description: 'Digitize all personnel files from OHCS departments',
      status: 'in_progress',
      priority: 'high',
      startDate: new Date(2024, 0, 15),
      targetEndDate: new Date(2024, 6, 30),
      createdBy: 'admin_001',
      assignedTeam: ['user_001', 'user_002', 'user_003'],
      mdaScope: ['OHCS', 'MOF'],
      totalFiles: 500,
      completedFiles: 187,
      inProgressFiles: 23,
      pendingFiles: 285,
      failedFiles: 5,
      qualityScore: 94.2,
      batches: [],
    },
    {
      id: 'proj_002',
      name: 'Legacy Records Migration',
      description: 'Convert legacy paper records from 1990-2010',
      status: 'in_progress',
      priority: 'normal',
      startDate: new Date(2024, 2, 1),
      targetEndDate: new Date(2024, 11, 31),
      createdBy: 'admin_001',
      assignedTeam: ['user_004', 'user_005'],
      mdaScope: ['MOH', 'MOE'],
      totalFiles: 1200,
      completedFiles: 340,
      inProgressFiles: 45,
      pendingFiles: 800,
      failedFiles: 15,
      qualityScore: 91.5,
      batches: [],
    },
    {
      id: 'proj_003',
      name: 'Emergency Document Backup',
      description: 'Priority digitization of critical documents',
      status: 'completed',
      priority: 'critical',
      startDate: new Date(2023, 10, 1),
      targetEndDate: new Date(2024, 0, 31),
      createdBy: 'admin_002',
      assignedTeam: ['user_001'],
      mdaScope: ['OHCS'],
      totalFiles: 150,
      completedFiles: 150,
      inProgressFiles: 0,
      pendingFiles: 0,
      failedFiles: 0,
      qualityScore: 98.7,
      batches: [],
    },
  ];
}

function generateMockBatches(projectId: string): DigitizationBatch[] {
  return [
    {
      id: `batch_${projectId}_1`,
      projectId,
      batchNumber: 1,
      status: 'completed',
      assignedTo: 'user_001',
      files: [],
      qualityCheckStatus: 'passed',
      startedAt: new Date(2024, 0, 16),
      completedAt: new Date(2024, 0, 20),
    },
    {
      id: `batch_${projectId}_2`,
      projectId,
      batchNumber: 2,
      status: 'scanning',
      assignedTo: 'user_002',
      files: [],
      qualityCheckStatus: 'pending',
      startedAt: new Date(2024, 0, 21),
    },
    {
      id: `batch_${projectId}_3`,
      projectId,
      batchNumber: 3,
      status: 'pending',
      assignedTo: 'user_003',
      files: [],
      qualityCheckStatus: 'pending',
    },
  ];
}

export function DigitizationPage() {
  const [projects, setProjects] = useState<DigitizationProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<DigitizationProject | null>(null);
  const [batches, setBatches] = useState<DigitizationBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'projects' | 'batches' | 'scanner' | 'quality'>('projects');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);

  // New project form state
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    priority: 'normal' as ProjectPriority,
    targetEndDate: '',
    estimatedFiles: 100,
    mdaScope: [] as string[],
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Using mock data for demonstration
        const mockProjects = generateMockProjects();
        setProjects(mockProjects);

        if (mockProjects.length > 0) {
          setSelectedProject(mockProjects[0]);
          setBatches(generateMockBatches(mockProjects[0].id));
        }
      } catch (err) {
        console.error('Failed to fetch digitization data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  function handleProjectSelect(project: DigitizationProject) {
    setSelectedProject(project);
    setBatches(generateMockBatches(project.id));
    setActiveTab('batches');
  }

  function handleCreateProject() {
    if (!newProject.name || !newProject.targetEndDate) return;

    const project = digitizationWorkflow.createProject({
      name: newProject.name,
      description: newProject.description,
      priority: newProject.priority,
      targetEndDate: new Date(newProject.targetEndDate),
      createdBy: 'current_user',
      mdaScope: newProject.mdaScope.length > 0 ? newProject.mdaScope : ['OHCS'],
      estimatedFiles: newProject.estimatedFiles,
    });

    setProjects([project, ...projects]);
    setShowNewProjectModal(false);
    setNewProject({
      name: '',
      description: '',
      priority: 'normal',
      targetEndDate: '',
      estimatedFiles: 100,
      mdaScope: [],
    });
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'active':
      case 'in_progress':
        return 'var(--ghana-green)';
      case 'completed':
      case 'approved':
        return 'var(--success)';
      case 'paused':
      case 'pending':
        return 'var(--warning)';
      case 'failed':
      case 'rejected':
        return 'var(--error)';
      default:
        return 'var(--text-muted)';
    }
  }

  function getPriorityColor(priority: ProjectPriority): string {
    switch (priority) {
      case 'critical':
        return 'var(--error)';
      case 'high':
        return 'var(--warning)';
      case 'normal':
        return 'var(--info)';
      case 'low':
        return 'var(--text-muted)';
      default:
        return 'var(--text-muted)';
    }
  }

  function calculateProgress(project: DigitizationProject): number {
    if (project.totalFiles === 0) return 0;
    return Math.round((project.completedFiles / project.totalFiles) * 100);
  }

  function formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Digitization Workflow...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>Registry Operations</div>
          <h1 className={styles.title}>Digitization Workflow</h1>
          <p className={styles.subtitle}>
            Manage scanning projects, track batches, and ensure quality control
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.primaryButton} onClick={() => setShowNewProjectModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Project
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        {[
          { id: 'projects', label: 'Projects', icon: '📁', count: projects.length },
          { id: 'batches', label: 'Batches', icon: '📦', count: batches.length },
          { id: 'scanner', label: 'Scan Station', icon: '📷' },
          { id: 'quality', label: 'Quality Review', icon: '✓' },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={styles.tabCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className={styles.content}>
          {/* Summary Stats */}
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{projects.length}</span>
              <span className={styles.statLabel}>Total Projects</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{projects.filter(p => p.status === 'in_progress').length}</span>
              <span className={styles.statLabel}>Active</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>
                {projects.reduce((sum, p) => sum + p.completedFiles, 0).toLocaleString()}
              </span>
              <span className={styles.statLabel}>Files Digitized</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>
                {projects.reduce((sum, p) => sum + p.pendingFiles, 0).toLocaleString()}
              </span>
              <span className={styles.statLabel}>Pending</span>
            </div>
          </div>

          {/* Projects Grid */}
          <div className={styles.projectsGrid}>
            {projects.map((project) => {
              const progress = calculateProgress(project);
              return (
                <div
                  key={project.id}
                  className={styles.projectCard}
                  onClick={() => handleProjectSelect(project)}
                >
                  <div className={styles.projectHeader}>
                    <div className={styles.projectTitleRow}>
                      <h3 className={styles.projectName}>{project.name}</h3>
                      <span
                        className={styles.priorityBadge}
                        style={{ backgroundColor: `${getPriorityColor(project.priority)}20`, color: getPriorityColor(project.priority) }}
                      >
                        {project.priority}
                      </span>
                    </div>
                    <span
                      className={styles.statusBadge}
                      style={{ backgroundColor: `${getStatusColor(project.status)}20`, color: getStatusColor(project.status) }}
                    >
                      {project.status}
                    </span>
                  </div>

                  <p className={styles.projectDesc}>{project.description}</p>

                  <div className={styles.projectProgress}>
                    <div className={styles.progressHeader}>
                      <span>Progress</span>
                      <span>{progress}%</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className={styles.progressStats}>
                      <span>{project.completedFiles} completed</span>
                      <span>{project.inProgressFiles} in progress</span>
                      <span>{project.pendingFiles} pending</span>
                    </div>
                  </div>

                  <div className={styles.projectMeta}>
                    <div className={styles.metaItem}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>Due: {formatDate(project.targetEndDate)}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                      </svg>
                      <span>Quality: {project.qualityScore}%</span>
                    </div>
                  </div>

                  <div className={styles.projectFooter}>
                    <div className={styles.mdaTags}>
                      {project.mdaScope.map((mda) => (
                        <span key={mda} className={styles.mdaTag}>{mda}</span>
                      ))}
                    </div>
                    <button className={styles.viewButton}>
                      View Details →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <div className={styles.content}>
          {selectedProject ? (
            <>
              {/* Project Info Banner */}
              <div className={styles.projectBanner}>
                <div className={styles.bannerContent}>
                  <h2 className={styles.bannerTitle}>{selectedProject.name}</h2>
                  <div className={styles.bannerStats}>
                    <span>{selectedProject.totalFiles} files</span>
                    <span>•</span>
                    <span>{calculateProgress(selectedProject)}% complete</span>
                    <span>•</span>
                    <span>{batches.length} batches</span>
                  </div>
                </div>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setActiveTab('projects')}
                >
                  Change Project
                </button>
              </div>

              {/* Batch Actions */}
              <div className={styles.batchActions}>
                <button className={styles.primaryButton}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Create Batch
                </button>
                <button className={styles.secondaryButton}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Import Files
                </button>
              </div>

              {/* Batches List */}
              <div className={styles.batchesList}>
                {batches.map((batch) => (
                  <div key={batch.id} className={styles.batchCard}>
                    <div className={styles.batchHeader}>
                      <div className={styles.batchInfo}>
                        <span className={styles.batchNumber}>Batch #{batch.batchNumber}</span>
                        <span
                          className={styles.batchStatus}
                          style={{ backgroundColor: `${getStatusColor(batch.status)}20`, color: getStatusColor(batch.status) }}
                        >
                          {batch.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className={styles.batchQuality}>
                        <span
                          className={styles.qualityStatus}
                          style={{ backgroundColor: `${getStatusColor(batch.qualityCheckStatus)}20`, color: getStatusColor(batch.qualityCheckStatus) }}
                        >
                          QC: {batch.qualityCheckStatus}
                        </span>
                      </div>
                    </div>

                    <div className={styles.batchDetails}>
                      <div className={styles.batchDetail}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>Assigned: {batch.assignedTo}</span>
                      </div>
                      <div className={styles.batchDetail}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                        </svg>
                        <span>Started: {formatDate(batch.startedAt || new Date())}</span>
                      </div>
                      {batch.completedAt && (
                        <div className={styles.batchDetail}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span>Completed: {formatDate(batch.completedAt)}</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.batchActions}>
                      <button className={styles.batchButton}>View Files</button>
                      <button className={styles.batchButton}>Generate Labels</button>
                      {batch.status === 'scanning' && (
                        <button className={styles.batchButtonPrimary}>Continue Scanning</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>📦</span>
              <h3>No Project Selected</h3>
              <p>Select a project from the Projects tab to view its batches</p>
              <button className={styles.primaryButton} onClick={() => setActiveTab('projects')}>
                View Projects
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scanner Tab */}
      {activeTab === 'scanner' && (
        <div className={styles.content}>
          <div className={styles.scannerLayout}>
            {/* Scanner Controls */}
            <div className={styles.scannerPanel}>
              <h3 className={styles.panelTitle}>Scan Station</h3>

              <div className={styles.scannerStatus}>
                <div className={styles.statusIndicator} data-status="ready" />
                <span>Scanner Ready</span>
              </div>

              <div className={styles.scannerControls}>
                <div className={styles.controlGroup}>
                  <label>Resolution</label>
                  <select className={styles.select}>
                    <option value="200">200 DPI</option>
                    <option value="300" selected>300 DPI (Recommended)</option>
                    <option value="600">600 DPI (High Quality)</option>
                  </select>
                </div>

                <div className={styles.controlGroup}>
                  <label>Color Mode</label>
                  <select className={styles.select}>
                    <option value="color">Color</option>
                    <option value="grayscale" selected>Grayscale</option>
                    <option value="bw">Black & White</option>
                  </select>
                </div>

                <div className={styles.controlGroup}>
                  <label>Document Type</label>
                  <select className={styles.select}>
                    <option value="letter">Letter/A4</option>
                    <option value="legal">Legal</option>
                    <option value="mixed">Mixed Sizes</option>
                  </select>
                </div>
              </div>

              <div className={styles.scanActions}>
                <button className={styles.scanButton}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                  Start Scan
                </button>
                <button className={styles.secondaryButton}>
                  Preview
                </button>
              </div>
            </div>

            {/* Current File Info */}
            <div className={styles.fileInfoPanel}>
              <h3 className={styles.panelTitle}>Current File</h3>

              <div className={styles.barcodeSection}>
                <div className={styles.barcodeDisplay}>
                  <div className={styles.barcodePlaceholder}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 5h2v14H3V5zm4 0h1v14H7V5zm3 0h2v14h-2V5zm4 0h3v14h-3V5zm5 0h2v14h-2V5z" />
                    </svg>
                    <span>Scan barcode to load file</span>
                  </div>
                </div>
                <button className={styles.secondaryButton}>
                  Manual Entry
                </button>
              </div>

              <div className={styles.fileDetails}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>File Number</span>
                  <span className={styles.detailValue}>—</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Staff Name</span>
                  <span className={styles.detailValue}>—</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Batch</span>
                  <span className={styles.detailValue}>—</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Expected Pages</span>
                  <span className={styles.detailValue}>—</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Scanned Pages</span>
                  <span className={styles.detailValue}>—</span>
                </div>
              </div>
            </div>

            {/* Scan Queue */}
            <div className={styles.queuePanel}>
              <h3 className={styles.panelTitle}>Scan Queue</h3>
              <div className={styles.queueList}>
                <div className={styles.queueEmpty}>
                  <span>No files in queue</span>
                  <span className={styles.queueHint}>Files will appear here when added to the current batch</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quality Review Tab */}
      {activeTab === 'quality' && (
        <div className={styles.content}>
          <div className={styles.qualityHeader}>
            <h3 className={styles.sectionTitle}>Quality Review Queue</h3>
            <div className={styles.filterButtons}>
              <button className={`${styles.filterButton} ${styles.active}`}>All</button>
              <button className={styles.filterButton}>Pending Review</button>
              <button className={styles.filterButton}>Issues Found</button>
              <button className={styles.filterButton}>Approved</button>
            </div>
          </div>

          <div className={styles.qualityStats}>
            <div className={styles.qualityStat}>
              <span className={styles.qualityValue}>23</span>
              <span className={styles.qualityLabel}>Pending Review</span>
            </div>
            <div className={styles.qualityStat}>
              <span className={styles.qualityValue}>156</span>
              <span className={styles.qualityLabel}>Approved Today</span>
            </div>
            <div className={styles.qualityStat}>
              <span className={styles.qualityValue}>7</span>
              <span className={styles.qualityLabel}>Issues Found</span>
            </div>
            <div className={styles.qualityStat}>
              <span className={styles.qualityValue}>94.2%</span>
              <span className={styles.qualityLabel}>Pass Rate</span>
            </div>
          </div>

          <div className={styles.reviewList}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.reviewCard}>
                <div className={styles.reviewPreview}>
                  <div className={styles.previewPlaceholder}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                </div>
                <div className={styles.reviewInfo}>
                  <div className={styles.reviewHeader}>
                    <span className={styles.reviewFile}>OHCS-2024-{String(i).padStart(3, '0')}</span>
                    <span className={styles.reviewStatus} data-status="pending">Pending Review</span>
                  </div>
                  <div className={styles.reviewMeta}>
                    <span>Staff: John Doe {i}</span>
                    <span>•</span>
                    <span>45 pages</span>
                    <span>•</span>
                    <span>Scanned 2h ago</span>
                  </div>
                  <div className={styles.reviewChecks}>
                    <span className={styles.check} data-passed="true">✓ Resolution</span>
                    <span className={styles.check} data-passed="true">✓ Orientation</span>
                    <span className={styles.check} data-passed="true">✓ Readability</span>
                    <span className={styles.check} data-passed={i !== 3}>
                      {i === 3 ? '✗' : '✓'} Page Order
                    </span>
                  </div>
                </div>
                <div className={styles.reviewActions}>
                  <button className={styles.approveButton}>Approve</button>
                  <button className={styles.rejectButton}>Flag Issue</button>
                  <button className={styles.viewButton}>View</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className={styles.modalOverlay} onClick={() => setShowNewProjectModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Create New Project</h2>
              <button className={styles.closeButton} onClick={() => setShowNewProjectModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Project Name *</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="e.g., 2024 Personnel Files Digitization"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Description</label>
                <textarea
                  className={styles.formTextarea}
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Brief description of the project scope..."
                  rows={3}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Priority</label>
                  <select
                    className={styles.formSelect}
                    value={newProject.priority}
                    onChange={(e) => setNewProject({ ...newProject, priority: e.target.value as ProjectPriority })}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Target End Date *</label>
                  <input
                    type="date"
                    className={styles.formInput}
                    value={newProject.targetEndDate}
                    onChange={(e) => setNewProject({ ...newProject, targetEndDate: e.target.value })}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Estimated Files</label>
                <input
                  type="number"
                  className={styles.formInput}
                  value={newProject.estimatedFiles}
                  onChange={(e) => setNewProject({ ...newProject, estimatedFiles: parseInt(e.target.value) || 0 })}
                  min={1}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>MDA Scope</label>
                <div className={styles.checkboxGroup}>
                  {['OHCS', 'MOF', 'MOH', 'MOE', 'MOTI', 'MLGRD'].map((mda) => (
                    <label key={mda} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={newProject.mdaScope.includes(mda)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewProject({ ...newProject, mdaScope: [...newProject.mdaScope, mda] });
                          } else {
                            setNewProject({ ...newProject, mdaScope: newProject.mdaScope.filter(m => m !== mda) });
                          }
                        }}
                      />
                      <span>{mda}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.secondaryButton} onClick={() => setShowNewProjectModal(false)}>
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleCreateProject}
                disabled={!newProject.name || !newProject.targetEndDate}
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
